/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SimpleIndefiniteProgressDialogComponent } from 'app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component';
import { SimpleSuccessErrorDialogComponent } from 'app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { ServiceInstance } from 'app/models/service-instance';
import { KissOutboundAttemptLineItemDto, KissOutboundState, KissOutboundStateInfo, KissService } from 'app/services/kiss.service';

@Component({
  templateUrl: './kiss-outbound-validation.dashboard.component.html',
  styleUrls: ['./kiss-outbound-validation.dashboard.component.css']
})
export class KissOutboundValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) 
  indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Portal whose dashboard is currently being displayed */
  portal: ServiceInstance;

  /** State currently being reflected in the UI */
  currentState: KissOutboundStateInfo;

  /** "Refresh" Loop Timer used to keep the UI updated */
  private refreshLoopTimer: any;

  /** Delay before refreshing the current attempt info */
  private readonly RefreshDelayInMs = 500;

  /** Boolean flag holding whether a user action is being performed.  Used to disable some UI controls. */
  isActionBeingPerformed = false;



  /** Constructor */
  constructor(
    private kissService: KissService,
    private route: ActivatedRoute,
    private router: Router
  ) { }
  


    /**
     * Initializes the component.  
     * Called automatically by the Angular Component Lifecycle.
     */
    ngOnInit(): void {
        this.route.data.subscribe(
          async (data: { 
              currentState: KissOutboundStateInfo,
              portal: ServiceInstance 
            }) => 
          {
            // Store the current state / selected portal
            this.currentState = data?.currentState;
            this.portal = data?.portal;

            // Redirect if the current state requires it
            switch (this.currentState?.state) {
              case KissOutboundState.WaitingOnContainerId: 
                this.router.navigate(['kiss', 'outbound-validation', this.portal.id, 'enter-info'], {});
                return;
              case KissOutboundState.AttemptBeingValidated: {
                break;
              }
              default: {
                this.simpleSuccessErrorDialogComponent.showErrorDialog('Error', `Unhandled state: ${this.currentState?.state}`);
                this.router.navigate(['kiss', 'outbound-validation', this.portal.id, 'enter-info'], {});
                return;
              }
            }
            
            // Start the "Refresh" Loop running in the background to keep the UI updated
            this.startRefreshLoop();
          });
    }

  /**
   * Destroys the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    // Stop the "Refresh" Loop to free up the resources / prevent it from continuing in the background
    this.stopRefreshLoop();
  }



  private startRefreshLoop(): void {
    // Stop the Refresh Loop if it is currently running
    this.stopRefreshLoop();
    
    // Start a new Refresh Loop
    this.refreshLoopTimer = setInterval(async () => { await this.refreshState(); }, this.RefreshDelayInMs);
  }

  async refreshState(): Promise<void> {
    if (this.portal?.id) {
      try {
        // Refresh the State
        this.currentState = await this.kissService.getOutboundState(this.portal.id).toPromise();

        // Forward to the appropriate screen (if needed)
        if (this.currentState?.state != null) {
          switch (this.currentState.state) {
            case KissOutboundState.WaitingOnContainerId: {
              this.router.navigate(['kiss', 'outbound-validation', this.portal.id, 'enter-info'], {});
              break;
            }
            case KissOutboundState.AttemptBeingValidated: {
              // Current Screen
              break;
            }
            default: {
              console.error(`Unhandled State: ${this.currentState.state}...`);
              break;
            }
          }
        }
      } catch (err) {
        console.error('Refresh failed', err);
      }
    }
  }

  private stopRefreshLoop(): void {
    if (this.refreshLoopTimer) {
       clearInterval(this.refreshLoopTimer);
    }
  }


  
  // Row Highlight Helpers
  isSuccessRow(lineItem: KissOutboundAttemptLineItemDto) {
    return lineItem.expected_quantity > 0 && lineItem.counted_quantity === lineItem.expected_quantity;
  }
  isPartialRow(lineItem: KissOutboundAttemptLineItemDto) {
    return lineItem.expected_quantity > 0 && lineItem.counted_quantity > 0 && lineItem.counted_quantity < lineItem.expected_quantity;
  }
  isErrorRow(lineItem: KissOutboundAttemptLineItemDto) {
    return (lineItem.expected_quantity === 0 && lineItem.counted_quantity > 0) || lineItem.counted_quantity > lineItem.expected_quantity;
  }
  isUnknownRow(lineItem: KissOutboundAttemptLineItemDto) {
    return lineItem.expected_quantity > 0 && (!lineItem.counted_quantity || lineItem.counted_quantity === 0);
  }
  trackByFunction(index: number, lineItem: KissOutboundAttemptLineItemDto): any {
    return `${lineItem.item}:${lineItem.batch}`;
  }



    // UI Actions
  onStartReaderClicked(): void {
    if (!this.portal?.id) return;
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Starting Reader...");
      this._onStartReaderClicked();
    }
  }
  async _onStartReaderClicked(): Promise<void> {
    try {
      await this.kissService.startOutboundReader(this.portal.id).toPromise();
      await this.refreshState();
    } finally { 
      this.indefiniteProgressDialog.hide();
      this.isActionBeingPerformed = false; 
    }
  }
  onStopReaderClicked(): void {
    if (!this.portal?.id) return;
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Stopping Reader...");
      this._onStopReaderClicked();
    }
  }
  async _onStopReaderClicked(): Promise<void> {
    try {
      await this.kissService.stopOutboundReader(this.portal.id).toPromise();
      await this.refreshState();
    } finally { 
      this.indefiniteProgressDialog.hide();
      this.isActionBeingPerformed = false; 
    }
  }
  onClearReadsClicked(): void {
    if (!this.portal?.id) return;
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Clearing Read(s)...");
      this._onClearReadsClicked();
    }
  }
  async _onClearReadsClicked(): Promise<void> {
    try {
      await this.kissService.clearOutboundReads(this.portal.id).toPromise();
      await this.refreshState();
    } finally { 
      this.indefiniteProgressDialog.hide();
      this.isActionBeingPerformed = false; 
    }
  }
  onClearAttemptClicked(): void {
    if (!this.portal?.id) return;
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Clearing Attempt...");
      this._onClearAttemptClicked();
    }
  }
  async _onClearAttemptClicked(): Promise<void> {
    try {
      await this.kissService.clearOutboundAttempt(this.portal.id).toPromise();
      await this.refreshState();
    } finally { 
      this.indefiniteProgressDialog.hide();
      this.isActionBeingPerformed = false; 
    }
  }
  onProcessAttemptClicked(): void {
    if (!this.portal?.id) return;
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Processing Attempt...");
      this._onProcessAttemptClicked();
    }
  }
  async _onProcessAttemptClicked(): Promise<void> {
    try {
      await this.kissService.processOutboundAttempt(this.portal.id).toPromise();
      await this.refreshState();
    } finally { 
      this.indefiniteProgressDialog.hide();
      this.isActionBeingPerformed = false; 
    }
  }

}
